import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';

import { AllowedTimes } from 'src/features/membershipType/types';
import AllowedTimeForm from 'src/features/membershipType/components/forms/allowedTime/AllowedTimeForm';
import {
    AllowedTimeFormSchemaValues,
    customAllowedTimeFormSchema,
    CustomAllowedTimeFormSchemaValues
} from 'src/features/membershipType/formSchema';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { useOnError } from 'src/utils/errors';
import { cn } from 'src/utils/shadcn';

interface AllowedTimeFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedAllowedTime?: AllowedTimes;
    allowedTimes?: AllowedTimes[];
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: AllowedTimeFormSchemaValues[],
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function AllowedTimeFormModal<TData>({
    selectedAllowedTime,
    allowedTimes,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: AllowedTimeFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    const onError = useOnError();

    const defaultValues = React.useMemo(
        () =>
            selectedAllowedTime
                ? {
                    ...selectedAllowedTime,
                    day_of_week: [selectedAllowedTime?.day_of_week],
                }
                : undefined,
        [selectedAllowedTime],
    );
    const form = useForm<CustomAllowedTimeFormSchemaValues>({
        resolver: zodResolver(customAllowedTimeFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [defaultValues, isModalOpen, form]);

    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: CustomAllowedTimeFormSchemaValues) => {
            const allowedTimeShapeData = values.day_of_week.map((week) => ({
                day_of_week: week,
                from_time: values.from_time,
                to_time: values.to_time,
            }));

            const updatedAllowedTime = selectedAllowedTime // edit mode
                ? allowedTimes?.map((item) => (item.id === selectedAllowedTime.id ? allowedTimeShapeData[0] : item))
                : [...(allowedTimes ?? []), ...allowedTimeShapeData];

            mutate(
                updatedAllowedTime ?? [],
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },

        [mutate, form, onError, allowedTimes, selectedAllowedTime, setIsModalOpen],
    );
    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={cn('ml-auto', className)}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader className="flex flex-row gap-2">
                    <SquareIcon icon={icon} />
                    <div>
                        <DialogTitle>{title}</DialogTitle>
                        <DialogDescription>{subtitle}</DialogDescription>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <AllowedTimeForm form={form} onSubmit={onSubmit} isEdit={!!selectedAllowedTime} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedAllowedTime && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default AllowedTimeFormModal;
